'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { logger } from '@libs/core';

const WEB_COLLAGE_SITECODE = 'petsmart';
const WEB_COLLAGE_LOAD_TIMEOUT = 15 * 1000;
const WEB_COLLAGE_SDK_RETRY_INTERVAL = 100;
const WEB_COLLAGE_CONTENT_SETTINGS = { 'power-page': { autoPlayAndStop: true, reload: true } };
const WEB_COLLAGE_SUBDOM_SELECTOR = 'syndigo-powerpage'; // HTML tag that Webcollage uses to render the content
const browserGlobal = globalThis as typeof globalThis & Pick<Window, 'flags' | 'Webcollage'>;

type WebcollageAvailability = {
    productId: string;
    found?: boolean;
};

type WebcollageLoad = {
    productId: string;
    started: boolean;
};

type Cleanup = () => void;
type WebcollageSdk = Window['Webcollage'];

function waitForWebcollageSdk(onReady: (sdk: WebcollageSdk) => void, onUnavailable: () => void): Cleanup {
    if (browserGlobal.Webcollage) {
        onReady(browserGlobal.Webcollage);
        return () => undefined;
    }

    let retry: ReturnType<typeof setInterval>;
    let timeout: ReturnType<typeof setTimeout>;

    const cleanup = () => {
        clearInterval(retry);
        clearTimeout(timeout);
    };

    const checkSdk = () => {
        if (!browserGlobal.Webcollage) return;

        cleanup();
        onReady(browserGlobal.Webcollage);
    };

    retry = setInterval(checkSdk, WEB_COLLAGE_SDK_RETRY_INTERVAL);
    timeout = setTimeout(() => {
        cleanup();
        onUnavailable();
    }, WEB_COLLAGE_LOAD_TIMEOUT);

    return cleanup;
}

function watchForWebcollageContent(target: Element, onFound: () => void, onUnavailable: () => void): Cleanup {
    let timeout: ReturnType<typeof setTimeout>;

    const observer = new MutationObserver(detectContent);

    function cleanup() {
        clearTimeout(timeout);
        observer.disconnect();
    }

    function detectContent() {
        if (!target.querySelector(WEB_COLLAGE_SUBDOM_SELECTOR)) return;

        cleanup();
        onFound();
    }

    observer.observe(target, { childList: true, subtree: true });
    timeout = setTimeout(onUnavailable, WEB_COLLAGE_LOAD_TIMEOUT);
    detectContent();

    return cleanup;
}

export function useWebcollage(productId: string) {
    const [availability, setAvailability] = useState<WebcollageAvailability>({ productId });
    const [initializationAttempt, setInitializationAttempt] = useState(0);
    const currentLoad = useRef<WebcollageLoad | undefined>(undefined);
    const webCollageFound = availability.productId === productId ? availability.found : undefined;
    const initializeWebcollage = useCallback(() => {
        const load = currentLoad.current;

        if (load?.productId !== productId || load?.started) return;

        setInitializationAttempt(attempt => attempt + 1);
    }, [productId]);

    useEffect(() => {
        const cleanups = new Set<Cleanup>();
        const target = document.querySelector('#wc-power-page');

        target?.replaceChildren();

        if (browserGlobal.flags?.IS_PERFORMANCE_MODE) {
            setAvailability({ productId, found: false });
            return;
        }

        if (!target) {
            setAvailability({ productId, found: false });
            return;
        }

        const load: WebcollageLoad = { productId, started: false };
        currentLoad.current = load;
        setAvailability({ productId });

        const loadProductContent = (sdk: WebcollageSdk) => {
            load.started = true;
            const stopWatching = watchForWebcollageContent(
                target,
                () => setAvailability({ productId, found: true }),
                () => setAvailability({ productId, found: false })
            );
            cleanups.add(stopWatching);

            try {
                sdk.loadProductContent(WEB_COLLAGE_SITECODE, String(productId), WEB_COLLAGE_CONTENT_SETTINGS);
            } catch (error) {
                load.started = false;
                stopWatching();
                logger.error('[useSalsify] Failed to load Webcollage content', error);
                setAvailability({ productId, found: false });
            }
        };

        cleanups.add(waitForWebcollageSdk(loadProductContent, () => setAvailability({ productId, found: false })));

        return () => {
            if (currentLoad.current === load) currentLoad.current = undefined;
            cleanups.forEach(cleanup => cleanup());
        };
    }, [initializationAttempt, productId]);

    return { initializeWebcollage, webCollageFound };
}
